import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PartnerCard from "@/components/PartnerCard";
import PartnerDetailDialog from "@/components/PartnerDetailDialog";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useT } from "@/context/LangContext";

export default function PartnersGrid({ featured = null, showFilters = true }) {
  const { t } = useT();
  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: ps }, { data: cs }] = await Promise.all([
          api.get("/partners" + (featured ? "?featured=true" : "")),
          api.get("/categories"),
        ]);
        setPartners(ps);
        setCategories(cs);
      } finally {
        setLoading(false);
      }
    })();
  }, [featured]);

  const filtered = useMemo(() => {
    return partners.filter((p) => {
      if (cat !== "all" && p.category !== cat) return false;
      if (search && !(p.name + " " + p.description + " " + p.offer).toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [partners, cat, search]);

  return (
    <div data-testid="partners-grid">
      {showFilters && (
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="partners-search-input"
              placeholder={t("partners_page.search_placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-11 h-12 rounded-full border-navy-900/10 bg-white"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            <button
              onClick={() => setCat("all")}
              data-testid="cat-all"
              className={`shrink-0 chip px-4 py-2 border ${cat === "all" ? "bg-navy-900 text-white border-navy-900" : "bg-white text-navy-900 border-navy-900/10 hover:border-navy-900/30"}`}
            >{t("partners_page.cat_all")}</button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`cat-${c}`}
                className={`shrink-0 chip px-4 py-2 border ${cat === c ? "bg-navy-900 text-white border-navy-900" : "bg-white text-navy-900 border-navy-900/10 hover:border-navy-900/30"}`}
              >{c}</button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="rounded-2xl bg-muted animate-pulse aspect-[4/5]" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">{t("partners_page.no_results")}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => (
            <PartnerCard key={p.id} partner={p} index={i} onClick={() => setActive(p)} />
          ))}
        </div>
      )}

      <PartnerDetailDialog partner={active} open={!!active} onOpenChange={(v) => !v && setActive(null)} />
    </div>
  );
}
